import { educationApi, type QuizAnswerInput } from './wikiApi';

/**
 * Оффлайн-очередь ответов теста (фаза 2.7): если отправка упала из-за сети,
 * ответы кладём в IndexedDB `wiki-pending-attempts` и досылаем при событии
 * `online` (Background Sync не используем — просто слушаем событие). Видео и
 * фото оффлайн не кешируются.
 */
const DB = 'wiki-offline';
const STORE = 'pending-attempts';

export interface PendingAttempt {
  key: string;
  quizId: string;
  attemptId?: string;
  answers: QuizAnswerInput[];
  savedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB недоступен')); return; }
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => { const db = req.result; if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' }); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB'));
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const r = fn(t.objectStore(STORE));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('IndexedDB'));
  }));
}

/** Сетевая ли ошибка (fetch упал, не HTTP-ответ). */
export function isNetworkError(e: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return e instanceof TypeError;
}

export async function savePendingAttempt(p: Omit<PendingAttempt, 'key' | 'savedAt'>): Promise<void> {
  const row: PendingAttempt = { ...p, key: p.attemptId ?? `${p.quizId}:${Date.now()}`, savedAt: Date.now() };
  await tx('readwrite', (s) => s.put(row));
}

export async function listPendingAttempts(): Promise<PendingAttempt[]> {
  try {
    return (await tx<PendingAttempt[]>('readonly', (s) => s.getAll())) ?? [];
  } catch {
    return [];
  }
}

/** Дослать всё из очереди; вернуть число отправленных. */
export async function flushPendingAttempts(): Promise<number> {
  const rows = await listPendingAttempts();
  let sent = 0;
  for (const r of rows) {
    try {
      await educationApi.submitQuiz(r.quizId, r.answers, r.attemptId);
      await tx('readwrite', (s) => s.delete(r.key));
      sent++;
    } catch (e) {
      if (isNetworkError(e)) break; // сети всё ещё нет — попробуем позже
      // Ответ сервера (попытка просрочена/уже сдана) — из очереди убираем.
      await tx('readwrite', (s) => s.delete(r.key)).catch(() => undefined);
    }
  }
  return sent;
}

/** Подписка на возврат сети (вызывать один раз при старте приложения). */
export function installOfflineAttemptSync(onFlushed?: (n: number) => void): void {
  if (typeof window === 'undefined') return;
  const run = (): void => { void flushPendingAttempts().then((n) => { if (n > 0) onFlushed?.(n); }); };
  window.addEventListener('online', run);
  // При старте — вдруг сеть уже вернулась, пока вкладка была закрыта.
  setTimeout(run, 3000);
}
